import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { handlePhoneClick } from '../../../utils/trackPhoneCall';
import { siteConfig } from '../../../config/site';
import { climatizacaoServicesList } from '../../../data/climatizacaoServices';
import { zonaDoCaminho } from '../../../data/zonas';
import { limpezaServicesList } from '../../../data/limpezaServices';

const Header: React.FC = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = React.useState(false);
  const [servicesOpen, setServicesOpen] = React.useState(false);

  const scrollTo = (selector: string) => {
    document.querySelector(selector)?.scrollIntoView({ behavior: 'smooth' });
    setOpen(false);
    setServicesOpen(false);
  };

  // Fechar dropdown de serviços quando o menu mobile fecha
  React.useEffect(() => {
    if (!open) {
      setServicesOpen(false);
    }
  }, [open]);

  const isDesentupimentosSite = siteConfig.domain === 'alphadesentupimentos.pt';
  const isEletricaSite = siteConfig.domain === 'alphaeletrica.pt' || siteConfig.domain === 'alphaeletric.pt';
  const isEletrodomesticosSite = siteConfig.domain === 'alphaservices.pt';
  const isPortoSite = siteConfig.domain === 'alphacanalizacoesporto.pt';
  const isClimatizacaoSite = siteConfig.domain === 'alphaclimatizacao.pt';
  const isCleanSite = siteConfig.domain === 'alphaclean.pt';

  // No site de climatização e limpeza usamos URLs curtos (/limpeza-condominios);
  // nos restantes mantém-se o padrão /servico/:id
  const serviceHref = (service: { id: string; path?: string }) =>
    service.path || `/servico/${service.id}`;

  // Estando dentro de uma localidade, o menu mantém-se nessa localidade:
  // em /ar-condicionado-amadora, "Manutenção" vai para /manutencao-ar-condicionado-amadora
  const zonaAtual = isClimatizacaoSite
    ? zonaDoCaminho(pathname, climatizacaoServicesList.map((s) => s.slug))
    : undefined;

  const services: { id: string; title: string; path?: string }[] = isCleanSite
    ? limpezaServicesList.map((s) => ({ id: s.id, title: s.navTitle, path: `/${s.slug}` }))
    : isClimatizacaoSite
    ? climatizacaoServicesList.map((s) => ({
        id: s.id,
        title: s.navTitle,
        path: zonaAtual ? `/${s.slug}-${zonaAtual.slug}` : `/${s.slug}`
      }))
    : isDesentupimentosSite ? [
    { id: 'esgotos', title: 'Desentupimento de Esgotos' },
    { id: 'sanitas', title: 'Desentupimento de Sanitas' },
    { id: 'lavatorios-pias', title: 'Desentupimento de Lavatórios e Pias' },
    { id: 'colunas-prediais', title: 'Desentupimento de Colunas Prediais' },
    { id: 'hidrojato', title: 'Desentupimento com Hidrojato' },
    { id: 'video-inspecao', title: 'Vídeo Inspeção de Tubulações' }
  ] : isEletricaSite ? [
    { id: 'reparacao-eletrica', title: 'Reparação Elétrica' },
    { id: 'iluminacao', title: 'Iluminação' },
    { id: 'instalacao-eletrica', title: 'Instalação Elétrica' },
    { id: 'quadro-eletrico', title: 'Quadro Elétrico' },
    { id: 'climatizacao-automacao', title: 'Climatização' },
    { id: 'reparacao-ar-condicionado', title: 'Reparação de Ar Condicionado' },
    { id: 'seguranca', title: 'Segurança' },
    { id: 'exterior-mobilidade', title: 'Exterior e Mobilidade' },
    { id: 'energia-renovavel', title: 'Energias Renováveis' },
    { id: 'estores', title: 'Estores' }
  ] : isEletrodomesticosSite ? [
    { id: 'placa-eletrica', title: 'Placa Elétrica' },
    { id: 'forno-eletrico', title: 'Forno Elétrico' },
    { id: 'fogao-eletrico', title: 'Fogão Elétrico' },
    { id: 'microondas', title: 'Microondas' },
    { id: 'maquina-loica', title: 'Máquina de Loiça' },
    { id: 'maquina-roupa', title: 'Máquina de Roupa' },
    { id: 'frigorifico', title: 'Frigorífico' },
    { id: 'termoacumulador', title: 'Termoacumulador' }
  ] : [
    { id: 'fugas', title: 'Detecção e Reparação de Fugas' },
    { id: 'reparacao', title: 'Reparação de Canalização' },
    { id: 'instalacao', title: 'Instalação de Canalização' },
    { id: 'manutencao', title: 'Manutenção Preventiva' },
    { id: 'remodelacao', title: 'Remodelação de Casas de Banho' }
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <div className="bg-white shadow-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-20 py-2 flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 cursor-pointer">
              <img
                src={
                  isCleanSite
                    ? '/images/alpha-clean-logo.webp'
                    : isClimatizacaoSite
                    ? '/images/alpha-climatizacao-logo.webp'
                    : isDesentupimentosSite
                      ? '/images/alpha-desentupimentos-logo.webp'
                      : isEletricaSite
                        ? '/images/alpha-eletrica-logo.webp'
                        : isEletrodomesticosSite
                          ? '/images/alpha services.webp'
                          : '/images/alpha-canalizacoes-logo.webp'
                }
                alt={siteConfig.logoAlt}
                className="h-20 w-20 rounded-full object-cover block"
                loading="eager"
              />
            </Link>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-3 text-sm font-medium text-gray-700">
              <Link
                to="/"
                className="hover:bg-gray-100 px-3 py-2 rounded-md hover:text-blue-700 transition-colors"
              >
                Home
              </Link>

              {/* Serviços Dropdown (Oculto para Eletrodomésticos se solicitado) */}
              {!isEletrodomesticosSite && (
                <div
                  className="relative"
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                >
                  <button className="hover:bg-gray-100 px-3 py-2 rounded-md hover:text-blue-700 inline-flex items-center gap-1 transition-colors pb-4 mb-[-1rem]">
                    Serviços
                    <i className={`ri-arrow-${servicesOpen ? 'up' : 'down'}-s-line`}></i>
                  </button>
                  {servicesOpen && (
                    <div className="absolute top-full left-0 pt-2 w-64 z-50">
                      <div className="bg-white rounded-lg shadow-xl border border-gray-200 py-2">
                        {zonaAtual && (
                          <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-400 border-b border-gray-100 mb-1">
                            {zonaAtual.preposicao} {zonaAtual.nome}
                          </div>
                        )}
                        {services.map((service) => (
                          <Link
                            key={service.id}
                            to={serviceHref(service)}
                            className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                            onClick={() => setServicesOpen(false)}
                          >
                            {service.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {isEletrodomesticosSite ? (
                null
              ) : (
                <>
                  {isClimatizacaoSite || isCleanSite ? null : (
                    <Link to="/blog" className="hover:bg-gray-100 px-3 py-2 rounded-md hover:text-blue-700 transition-colors">Blog</Link>
                  )}
                  {isPortoSite && (
                    <Link to="/pintura-pladur" className="hover:bg-gray-100 px-3 py-2 rounded-md hover:text-blue-700 transition-colors">Pintura & Pladur</Link>
                  )}
                  <Link to="/sobre-nos" className="hover:bg-gray-100 px-3 py-2 rounded-md hover:text-blue-700 transition-colors">Sobre Nós</Link>
                  {!isClimatizacaoSite && (
                    <Link to="/contato" className="hover:bg-gray-100 px-3 py-2 rounded-md hover:text-blue-700 transition-colors">Contato</Link>
                  )}
                </>
              )}
              <a
                href={`tel:${siteConfig.phone}`}
                onClick={handlePhoneClick}
                className="ml-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors inline-flex items-center gap-2 shadow-sm"
              >
                <i className="ri-phone-line"></i>
                Ligar
              </a>
            </nav>

            {/* Mobile */}
            <button
              aria-label="Abrir menu"
              className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
              onClick={() => setOpen((v) => !v)}
            >
              <i className={`ri-${open ? 'close' : 'menu'}-line text-xl`}></i>
            </button>
          </div>
        </div>
        {/* Mobile drawer */}
        {open && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-3 text-gray-700">
              <Link
                to="/"
                className="text-left py-2"
                onClick={() => setOpen(false)}
              >
                Home
              </Link>

              {/* Serviços Mobile (Oculto para Eletrodomésticos) */}
              {!isEletrodomesticosSite && (
                <div>
                  <button
                    className="text-left py-2 w-full flex items-center justify-between"
                    onClick={() => setServicesOpen(!servicesOpen)}
                  >
                    <span>Serviços</span>
                    <i className={`ri-arrow-${servicesOpen ? 'up' : 'down'}-s-line`}></i>
                  </button>
                  {servicesOpen && (
                    <div className="pl-4 mt-2 space-y-2">
                      {zonaAtual && (
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 pt-1">
                          {zonaAtual.preposicao} {zonaAtual.nome}
                        </div>
                      )}
                      {services.map((service) => (
                        <Link
                          key={service.id}
                          to={serviceHref(service)}
                          className="block py-2 text-gray-600 hover:text-blue-700"
                          onClick={() => {
                            setOpen(false);
                            setServicesOpen(false);
                          }}
                        >
                          {service.title}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {isEletrodomesticosSite ? (
                null
              ) : (
                <>
                  {isClimatizacaoSite || isCleanSite ? null : (
                    <Link to="/blog" className="text-left py-2" onClick={() => setOpen(false)}>Blog</Link>
                  )}
                  {isPortoSite && (
                    <Link to="/pintura-pladur" className="text-left py-2" onClick={() => setOpen(false)}>Pintura & Pladur</Link>
                  )}
                  <Link to="/sobre-nos" className="text-left py-2" onClick={() => setOpen(false)}>Sobre Nós</Link>
                  {!isClimatizacaoSite && (
                    <Link to="/contato" className="text-left py-2" onClick={() => setOpen(false)}>Contato</Link>
                  )}
                </>
              )}
              <a href={`tel:${siteConfig.phone}`} onClick={handlePhoneClick} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2">
                <i className="ri-phone-line"></i>
                Ligar agora
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;


